/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import { Form, Col } from 'react-bootstrap';
import { useController, useFormContext } from 'react-hook-form';
import apiClient from 'api/client';

/**
 * DynamicDropdown component for handling both standard and dependent dropdowns
 * with support for edit mode without label fields
 *
 * @param {Object} props Component properties
 * @param {Object} props.metadata Field metadata including options, dependencies, etc.
 * @param {boolean} props.isDisabled Whether the dropdown is disabled
 */
const DynamicDropdown = ({
  metadata,
  isDisabled = false,
  setDropdownOptions
}) => {
  const [options, setOptions] = useState(metadata.options || []);
  const [isLoading, setIsLoading] = useState(false);
  const previousDependentValue = useRef(null);
  const optionsLoaded = useRef(false);
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const isInitialMount = useRef(true);

  // Get form context
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues
  } = useFormContext();

  // IMPORTANT: Ensure consistent default value handling
  // Use null instead of empty string for consistency with validation
  const { field } = useController({
    name: metadata.id,
    control,
    defaultValue:
      metadata.defaultValue !== undefined
        ? metadata.defaultValue
        : metadata.multiSelect
        ? []
        : null
  });

  // Watch the dependent field's value if this is a dependent dropdown
  const dependentValue = metadata.dependsOn ? watch(metadata.dependsOn) : null;

  // Debug logging in production
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [
    metadata.id,
    field.value,
    options,
    metadata.defaultValue,
    dependentValue
  ]);

  // Ensure options are shared with parent component
  useEffect(() => {
    if (setDropdownOptions && options.length > 0) {
      setDropdownOptions(metadata.id, options);
    }
  }, [options, metadata.id, setDropdownOptions]);

  // If there's a default value and options are available, make sure the value is set
  useEffect(() => {
    // Only run this once after initial options are loaded
    if (
      !optionsLoaded.current &&
      metadata.defaultValue !== undefined &&
      metadata.defaultValue !== null
    ) {
      // For single-select dropdowns
      if (!metadata.multiSelect) {
        const valueExists = options.some(
          option => option.value === metadata.defaultValue
        );

        if (valueExists) {
          // Make sure the field value is set to the default
          setValue(metadata.id, metadata.defaultValue);

          // Find and set the label
          const option = options.find(
            opt => opt.value === metadata.defaultValue
          );
          if (option) {
            setValue(`${metadata.id}_label`, option.label);
          }
        }
      }
      // For multi-select dropdowns
      else if (
        Array.isArray(metadata.defaultValue) &&
        metadata.defaultValue.length > 0
      ) {
        // Ensure all default values have corresponding options
        const validDefaultValues = metadata.defaultValue.filter(value =>
          options.some(option => option.value === value)
        );

        if (validDefaultValues.length > 0) {
          setValue(metadata.id, validDefaultValues);

          // Set labels for each selected value
          validDefaultValues.forEach(value => {
            const option = options.find(opt => opt.value === value);
            if (option) {
              setValue(`${metadata.id}_label_${value}`, option.label);
            }
          });
        }
      }
    }

    // Mark options as loaded if we have options
    if (options.length > 0 && !optionsLoaded.current) {
      optionsLoaded.current = true;
    }
  }, [
    metadata.defaultValue,
    metadata.id,
    metadata.multiSelect,
    options,
    setValue
  ]);

  // Initialize component - resolve labels for initial values
  useEffect(() => {
    // Skip if options are already loaded or if there's no value
    if (optionsLoaded.current || !field.value) return;

    // For multi-select fields
    if (
      metadata.multiSelect &&
      Array.isArray(field.value) &&
      field.value.length > 0
    ) {
      // Check if each value has a corresponding option with a label
      const valuesWithoutLabels = field.value.filter(
        value => !options.some(option => option.value === value)
      );

      // If we have values without labels, try to add them from the main options
      if (valuesWithoutLabels.length > 0) {
        const updatedOptions = [...options];

        // For each value without a label, try to find it in metadata.options
        valuesWithoutLabels.forEach(value => {
          // First check in the provided options
          if (metadata.options && metadata.options.length > 0) {
            const existingOption = metadata.options.find(
              opt => opt.value === value
            );

            if (existingOption) {
              // Add option if it doesn't exist in current options
              if (!updatedOptions.some(opt => opt.value === value)) {
                updatedOptions.push(existingOption);
              }

              // Create label field for the value
              setValue(`${metadata.id}_label_${value}`, existingOption.label);
              return;
            }
          }

          // If not found, use the value itself as the label
          if (!updatedOptions.some(opt => opt.value === value)) {
            updatedOptions.push({ value, label: String(value) });
          }

          // Create label field
          setValue(`${metadata.id}_label_${value}`, String(value));
        });

        setOptions(updatedOptions);
      }
    }
    // For single select fields
    else if (!metadata.multiSelect && field.value) {
      const valueExists = options.some(option => option.value === field.value);

      if (!valueExists) {
        let updatedOptions = [...options];
        let optionLabel = String(field.value);

        // Try to find the option in metadata.options
        if (metadata.options && metadata.options.length > 0) {
          const existingOption = metadata.options.find(
            opt => opt.value === field.value
          );

          if (existingOption) {
            updatedOptions.push(existingOption);
            optionLabel = existingOption.label;
          } else {
            // If no option exists, use the value as the label
            updatedOptions.push({
              value: field.value,
              label: String(field.value)
            });
          }
        } else {
          // No metadata options, use value as label
          updatedOptions.push({
            value: field.value,
            label: String(field.value)
          });
        }

        setOptions(updatedOptions);
        setValue(`${metadata.id}_label`, optionLabel);
      }
    }

    // Mark options as loaded
    if (field.value) {
      optionsLoaded.current = true;
    }
  }, [
    field.value,
    options,
    metadata.id,
    metadata.multiSelect,
    metadata.options,
    setValue,
    optionsLoaded
  ]);

  // Load options for dependent dropdown when parent value changes
  useEffect(() => {
    // Skip if this isn't a dependent dropdown or if no dependentValue exists
    if (
      !metadata.dependsOn ||
      dependentValue === null ||
      dependentValue === undefined ||
      !metadata.endpoint
    ) {
      return;
    }

    // Skip if the dependent value hasn't changed
    if (previousDependentValue.current === dependentValue) {
      return;
    }

    // Update reference for next comparison
    previousDependentValue.current = dependentValue;

    const fetchOptions = async () => {
      setIsLoading(true);

      try {
        const endpoint = metadata.endpoint.replace('{id}', dependentValue);
        const response = await apiClient.get(`/digitization/v1${endpoint}`);

        if (response.data && response.data.results) {
          const newOptions = response.data.results;
          const currentValue = field.value;

          if (metadata.multiSelect) {
            // ✅ Multi-Select: Retain values not in the new options
            if (Array.isArray(currentValue) && currentValue.length > 0) {
              const valuesNotInNewOptions = currentValue.filter(
                value => !newOptions.some(opt => opt.value === value)
              );

              if (valuesNotInNewOptions.length > 0) {
                const mergedOptions = [...newOptions];

                valuesNotInNewOptions.forEach(value => {
                  const existingOption = options.find(
                    opt => opt.value === value
                  );
                  if (existingOption) {
                    mergedOptions.push(existingOption);
                  } else {
                    mergedOptions.push({ value, label: String(value) });
                  }
                });

                setOptions(mergedOptions);
              } else {
                setOptions(newOptions);
              }
            } else {
              setOptions(newOptions);
            }
          } else {
            // ✅ Single-Select: Reset if the value is not in the new options
            const isValueInNewOptions = newOptions.some(
              opt => opt.value === currentValue
            );

            if (!isValueInNewOptions && currentValue) {
              setValue(metadata.id, null);
              setValue(`${metadata.id}_label`, '');
            }

            setOptions(newOptions);
          }

          optionsLoaded.current = true;
        } else {
          console.warn(
            `Unexpected response format for ${metadata.id}:`,
            response.data
          );
          setOptions([]);

          // Reset value if we get an empty response
          if (field.value) {
            setValue(metadata.id, metadata.multiSelect ? [] : null);
            setValue(`${metadata.id}_label`, '');
          }
        }
      } catch (error) {
        console.error(
          `Error fetching dropdown options for ${metadata.id}:`,
          error
        );

        // Only reset if field has a value and options are empty
        if (field.value && options.length === 0) {
          setValue(metadata.id, metadata.multiSelect ? [] : null);
          setValue(`${metadata.id}_label`, '');
        }

        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [
    metadata.dependsOn,
    dependentValue,
    metadata.endpoint,
    metadata.id,
    metadata.multiSelect,
    setValue,
    field.value,
    options,
    tns_tenant
  ]);

  // Handle dropdown change
  const handleChange = e => {
    const value = e.target.value === '' ? null : e.target.value;

    // Update the form value
    field.onChange(value);

    // Store the label for display purposes
    if (value) {
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        // Store the label separately for display in tables
        setValue(`${metadata.id}_label`, selectedOption.label);
      }
    } else {
      setValue(`${metadata.id}_label`, '');
    }

    // If there are dependent fields, reset them
    if (metadata.resetDependents && Array.isArray(metadata.resetDependents)) {
      const formValues = getValues(); // Use getValues instead of watch for performance

      metadata.resetDependents.forEach(dependentFieldId => {
        if (formValues[dependentFieldId] !== undefined) {
          console.log(`Resetting dependent field ${dependentFieldId}`);

          // Reset the field value
          setValue(dependentFieldId, metadata.multiSelect ? [] : null);

          // Reset the label
          setValue(`${dependentFieldId}_label`, '');
        }
      });
    }
  };

  // Handle multi-select dropdown change
  const handleMultiSelectChange = e => {
    // Get all selected options
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      option => option.value
    );

    // Update the form value
    field.onChange(selectedOptions);

    // Store labels for each selected value
    selectedOptions.forEach(value => {
      const option = options.find(opt => opt.value === value);
      if (option) {
        setValue(`${metadata.id}_label_${value}`, option.label);
      }
    });
  };

  return (
    <Form.Group as={Col} sm={12} md={12} className="mb-3">
      <Form.Label>
        {metadata.label}
        {metadata?.validation?.required && (
          <span className="text-danger ms-1">*</span>
        )}
      </Form.Label>

      {metadata.multiSelect ? (
        <Form.Select
          multiple
          value={Array.isArray(field.value) ? field.value : []}
          disabled={isDisabled || isLoading || !metadata.isEditable}
          isInvalid={!!errors[metadata.id]}
          onChange={handleMultiSelectChange}
          style={{ height: '120px' }}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      ) : (
        <Form.Select
          value={field.value ?? ''}
          disabled={isDisabled || isLoading || !metadata.isEditable}
          isInvalid={!!errors[metadata.id]}
          onChange={handleChange}
        >
          <option value="">
            {isLoading
              ? 'Loading options...'
              : metadata.placeholder || 'Select an option'}
          </option>

          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )}

      {/* {isLoading && <div className="text-muted mt-1">Loading options...</div>} */}

      {errors[metadata.id] && (
        <Form.Control.Feedback type="invalid">
          {errors[metadata.id]?.message}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default DynamicDropdown;
